import type { ReportItem, ReportSection } from '@neo/blocks/sidebar/app-sidebar-reports'
import type { LucideIcon } from 'lucide-react'
import { AI_AGENTS_SECTION, USAGE_SECTION } from 'v2/pages/reports/report-sections'
import {
  LOCKED_TOOLTIP,
  openBuddyEnterpriseRequest,
} from 'v2/shared/utils/buddy-enterprise-request'
import type { IRoute } from 'v2/types'
import {
  HOME_LINK,
  isNavGroup,
  isNavReports,
  type NavContext,
  type NavEntryConfig,
  type NavLinkConfig,
  type NavReportsConfig,
  SUPPORT_LINK,
  WORKSPACES,
  Workspace,
} from './workspace-nav'

export interface ResolvedNavLink {
  key: string
  title: string
  path: string
  icon?: LucideIcon
  locked?: boolean
  lockedTooltip?: string
  onLockedClick?: () => void
}

export interface ResolvedNavGroup {
  key: string
  title: string
  icon: LucideIcon
  items: ResolvedNavLink[]
}

export type ResolvedNavEntry = ResolvedNavLink | ResolvedNavGroup

export interface ResolvedNavSection {
  label: string
  entries: ResolvedNavEntry[]
}

export interface ResolvedWorkspace {
  id: Workspace
  title: string
  description: string
  icon: LucideIcon
  sections: ResolvedNavSection[]
}

export interface ResolvedNav {
  workspaces: ResolvedWorkspace[]
  home?: ResolvedNavLink
  support?: ResolvedNavLink
}

export const isResolvedGroup = (entry: ResolvedNavEntry): entry is ResolvedNavGroup =>
  'items' in entry

export const matchesPath = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`)

export const isLinkActive = (pathname: string, link: ResolvedNavLink) =>
  matchesPath(pathname, link.path)

/** The group item for this page: the most specific match, so /flow stays dark on /flow/runs. */
export const activeItemKey = (pathname: string, items: ResolvedNavLink[]) =>
  items
    .filter((item) => isLinkActive(pathname, item))
    .reduce<ResolvedNavLink | undefined>(
      (best, item) => (best && best.path.length >= item.path.length ? best : item),
      undefined,
    )?.key

const findAllowedRoute = (routes: IRoute[], path: string) =>
  routes.find((route) => route.path === path && !route.notAllowed)

export const isRouteAllowed = (routes: IRoute[], path: string) => !!findAllowedRoute(routes, path)

// The usage reports under the sidebar's names and order, any other after them; the report pages
// keep their own names.
const USAGE_NAV_TITLES: Record<string, string> = {
  '/analytics/usage/overview': 'Overall spend',
  '/analytics/usage/voice': 'Voice usage',
  '/analytics/usage/messaging': 'Messaging usage',
}

const toReportLink = (item: ReportItem, title = item.title): ResolvedNavLink => ({
  key: item.url,
  title,
  path: item.url,
  icon: item.icon,
})

// Voice Agents lists the AI Agents reports; Telephony lists Usage first, then the other sections.
const reportLinks = (
  sections: ReportSection[],
  reports: NavReportsConfig['reports'],
): ResolvedNavLink[] => {
  const items = (label: string) => sections.find((s) => s.label === label)?.items ?? []
  if (reports === 'agents') return items(AI_AGENTS_SECTION).map((item) => toReportLink(item))
  const order = Object.keys(USAGE_NAV_TITLES)
  const rank = (url: string) => (order.includes(url) ? order.indexOf(url) : order.length)
  const usageLinks = [...items(USAGE_SECTION)]
    .sort((a, b) => rank(a.url) - rank(b.url))
    .map((item) => toReportLink(item, USAGE_NAV_TITLES[item.url]))
  const others = sections.filter((s) => s.label !== AI_AGENTS_SECTION && s.label !== USAGE_SECTION)
  return [...usageLinks, ...others.flatMap((s) => s.items.map((item) => toReportLink(item)))]
}

/**
 * Builds the sidebar for this session in one pass: an item shows when the user may open its
 * gate route and its own rule (`visible`) allows it, and is ghosted when the route or the item
 * is locked.
 */
export const resolveWorkspaceNav = (
  routes: IRoute[],
  reportSections: ReportSection[],
  ctx: NavContext,
): ResolvedNav => {
  const toLink = (link: NavLinkConfig, gateRoute: IRoute): ResolvedNavLink => {
    const base = {
      key: link.key,
      title: link.title,
      path: link.path,
      icon: link.icon,
    }
    if (gateRoute.locked) {
      const { locked, lockedTooltip, onLockedClick } = gateRoute
      return { ...base, locked, lockedTooltip, onLockedClick }
    }
    const source = link.lockSource?.(ctx)
    if (!source) return base
    return {
      ...base,
      locked: true,
      lockedTooltip: LOCKED_TOOLTIP,
      onLockedClick: () => openBuddyEnterpriseRequest({ source }),
    }
  }

  const resolveLink = (link: NavLinkConfig, inherited?: IRoute) => {
    const gateRoute = link.gate ? findAllowedRoute(routes, link.gate) : inherited
    if (!gateRoute || (link.visible && !link.visible(ctx))) return undefined
    return toLink(link, gateRoute)
  }

  const resolveEntry = (entry: NavEntryConfig): ResolvedNavEntry[] => {
    if (isNavReports(entry)) {
      if (!findAllowedRoute(routes, entry.gate)) return []
      const links = reportLinks(reportSections, entry.reports)
      if (entry.reports === 'agents' || links.length === 0) return links
      return [{ key: entry.key, title: entry.title, icon: entry.icon, items: links }]
    }
    if (isNavGroup(entry)) {
      const gateRoute = findAllowedRoute(routes, entry.gate)
      if (!gateRoute) return []
      // A locked group has nothing to expand: it renders as one ghosted item.
      if (gateRoute.locked) return [toLink({ ...entry, path: entry.items[0].path }, gateRoute)]
      const items = entry.items.flatMap((item) => resolveLink(item, gateRoute) ?? [])
      if (items.length) return [{ key: entry.key, title: entry.title, icon: entry.icon, items }]
      // Every page hidden (Compliance's upgrade card): one link to the section, as before.
      return [toLink({ ...entry, path: entry.gate }, gateRoute)]
    }
    const link = resolveLink(entry)
    return link ? [link] : []
  }

  const home = resolveLink(HOME_LINK)
  const workspaces = WORKSPACES.flatMap((workspace): ResolvedWorkspace[] => {
    if (workspace.gate && !findAllowedRoute(routes, workspace.gate)) return []
    const sections = workspace.sections
      .map((section) => ({ label: section.label, entries: section.entries.flatMap(resolveEntry) }))
      .filter((section) => section.entries.length > 0)
    if (!home && sections.length === 0) return []
    const { id, title, description, icon } = workspace
    return [{ id, title, description, icon, sections }]
  })
  return { workspaces, home, support: resolveLink(SUPPORT_LINK) }
}

const ownerOf = (pathname: string): Workspace | undefined => {
  let owner: Workspace | undefined
  let longest = 0
  for (const workspace of WORKSPACES) {
    for (const prefix of workspace.ownedPrefixes) {
      if (prefix.length > longest && matchesPath(pathname, prefix)) {
        owner = workspace.id
        longest = prefix.length
      }
    }
  }
  return owner
}

/**
 * A page that belongs to one workspace selects it; a shared page keeps the current one.
 * With nothing current, Telephony is the fallback, then the first in switcher order.
 */
export const getWorkspace = (
  pathname: string,
  current: Workspace | undefined,
  available: Workspace[],
): Workspace | undefined => {
  const owner = ownerOf(pathname)
  if (owner && available.includes(owner)) return owner
  if (current && available.includes(current)) return current
  return available.includes(Workspace.telephony) ? Workspace.telephony : available[0]
}
